import {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {Swiper, SwiperProps} from 'swiper/react';
import {Swiper as SwiperType} from 'swiper';
import {Pagination, Navigation, Autoplay} from 'swiper/modules';
import cn from 'classnames';
import {ArrowNextIcon} from '@/_application/icons/arrow-next';
import {ArrowPrevIcon} from '@/_application/icons/arrow-prev';
import {padSingleDigitNumberWithZero} from '@/_application/core/utils/common';
import ArrowNarrowPrevIcon from '@/_application/modules/festival/shared/icons/arrow-narrow-prev-icon';
import ArrowNarrowNextIcon from '@/_application/modules/festival/shared/icons/arrow-narrow-next-icon';
import styles from './custom-swiper.module.css';
interface IProps extends SwiperProps {
    className?: string;
    hasPagination?: boolean;
    hasNavigation?: boolean;
    buttonPrevClassName?: string;
    buttonNextClassName?: string;
    type?: 'shortcut' | 'default';
    data?: any[];
    narrow?: boolean;
    slidePerGroup?: number;
}

const CustomSwiper = ({
    children,
    className,
    hasPagination,
    hasNavigation,
    buttonPrevClassName,
    buttonNextClassName,
    type = 'default',
    data,
    slidePerGroup,
    narrow,
    ...props
}: PropsWithChildren<IProps>) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [totalSlides, setTotalSlides] = useState(0);
    const [navigationNeeded, setNavigationNeeded] = useState(true);
    const swiperRef = useRef<SwiperType | undefined>(undefined);

    const handleSlideNext = () => {
        if (swiperRef?.current?.isEnd) {
            swiperRef.current.slideTo(0);
            return;
        }
        if (slidePerGroup) {
            swiperRef?.current?.slideTo(activeSlide + slidePerGroup - 1);
        } else {
            swiperRef?.current?.slideNext();
        }
    };

    const handleSlidePrev = () => {
        if (activeSlide === 1) {
            swiperRef?.current?.slideTo(totalSlides);
            return;
        }
        if (slidePerGroup) {
            swiperRef?.current?.slideTo(activeSlide - slidePerGroup - 1);
        } else {
            swiperRef?.current?.slidePrev();
        }
    };

    const handleSlideChange = () => {
        const activeIndex = swiperRef?.current?.activeIndex ?? 0;
        setActiveSlide(activeIndex + 1);

        const slides = swiperRef?.current?.slides ?? [];
        setTotalSlides(slides.length);
    };

    const handleSwiperInit = (swiper: SwiperType) => {
        swiperRef.current = swiper;
        setTotalSlides(swiper.slides.length);
        setActiveSlide(swiper.activeIndex + 1);
    };

    // Check if there are enough slides to show navigation buttons
    useEffect(() => {
        const swiper = swiperRef.current;
        if (swiper && data && data?.length > 0) {
            const isNavigationNeeded = Number(swiper.slides.length) > Number(swiper.params.slidesPerView);
            setNavigationNeeded(isNavigationNeeded);
        }
    }, [data]);

    useEffect(() => {
        swiperRef?.current?.on('slideChange', handleSlideChange);
        return () => {
            swiperRef?.current?.off('slideChange', handleSlideChange);
        };
    }, []);

    const buttonPrevClass = cn(
        buttonPrevClassName,
        {
            [styles.hasNavigation]: hasNavigation,
            [styles.shortcut]: type === 'shortcut',
            [styles.navButton]: hasNavigation && type !== 'shortcut',
            [styles.hidden]: !hasNavigation,
            [styles.leftDefault]: !buttonPrevClassName
        },
        styles.base
    );
    const buttonNextClass = cn(
        buttonNextClassName,
        {
            [styles.hasNavigation]: hasNavigation,
            [styles.shortcut]: type === 'shortcut',
            [styles.navButton]: hasNavigation && type !== 'shortcut',
            [styles.hidden]: !hasNavigation,
            [styles.rightDefault]: !buttonNextClassName
        },
        styles.base
    );
    const customPaginationClass = cn(
        'swiper-pagination-custom bg-KPZ_Blue-Dark opacity-50 absolute left-[calc(100%-72px)]! font-bold flex items-center justify-center bottom-[20px]! z-1 w-12! py-0.5 rounded-full text-xs text-white select-none',
    );

    const classess = cn('overflow-y-visible! overflow-x-clip!', className);

    return (
        <div className="relative z-0">
            <Swiper
                onBeforeInit={handleSwiperInit}
                onInit={handleSwiperInit}
                onSlideChange={handleSlideChange}
                modules={[Navigation, Pagination, Autoplay]}
                navigation={{
                    prevEl: '.swiper-prev-custom',
                    nextEl: '.swiper-next-custom',
                }}
                className={classess}
                {...props}>
                {children}

                {hasPagination && activeSlide > 0 && totalSlides > 1 && (
                    <div className={customPaginationClass}>
                        {`${padSingleDigitNumberWithZero(activeSlide)}/${padSingleDigitNumberWithZero(totalSlides)}`}
                    </div>
                )}
            </Swiper>

            {navigationNeeded && (
                <>
                    <button aria-label="Slide trước" className={buttonPrevClass} onClick={handleSlidePrev}>
                        {narrow ? (
                            <ArrowNarrowPrevIcon isDisable={false} />
                        ) : (
                            <ArrowPrevIcon className="h-3 w-2 transition-colors group-hover:text-white" />
                        )}
                    </button>
                    <button aria-label="Slide tiếp theo" className={buttonNextClass} onClick={handleSlideNext}>
                        {narrow ? (
                            <ArrowNarrowNextIcon isDisable={false} />
                        ) : (
                            <ArrowNextIcon className="h-3 w-2 transition-colors group-hover:text-white" />
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

export default CustomSwiper;
